import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject, debounceTime} from 'rxjs';

export interface BulkImageResult {
    success: boolean;
    imageBase64: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class ImageBatchService {
    // Pages that load everything client-side (no server pagination) can mount hundreds of
    // <app-image> instances in a single tick — capping the batch keeps each request small
    // and lets earlier chunks resolve while later ones are still in flight, instead of one
    // huge request blocking every image on the page.
    private readonly MAX_BATCH_SIZE = 40;

    private pendingUrls = new Map<string, Subject<BulkImageResult>>();
    private batchTrigger = new Subject<void>();

    constructor(private http: HttpClient) {
        this.batchTrigger.pipe(debounceTime(20)).subscribe(() => this.flush());
    }

    requestImage(url: string): Observable<BulkImageResult> {
        if (!this.pendingUrls.has(url)) {
            this.pendingUrls.set(url, new Subject<BulkImageResult>());
        }
        this.batchTrigger.next();
        return this.pendingUrls.get(url)!.asObservable();
    }

    private flush() {
        if (this.pendingUrls.size === 0) return;

        const urls = Array.from(this.pendingUrls.keys());
        const subjects = new Map(this.pendingUrls);
        this.pendingUrls.clear();

        for (let i = 0; i < urls.length; i += this.MAX_BATCH_SIZE) {
            this.sendBatch(urls.slice(i, i + this.MAX_BATCH_SIZE), subjects);
        }
    }

    private sendBatch(urls: string[], subjects: Map<string, Subject<BulkImageResult>>) {
        this.http.post<{ success: boolean; data: { [url: string]: BulkImageResult } }>(
            '/api/images/bulk-status',
            { image_urls: urls }
        ).subscribe({
            next: (response) => {
                for (const url of urls) {
                    const result = response.data[url] ?? { success: false, imageBase64: null };
                    subjects.get(url)?.next(result);
                    subjects.get(url)?.complete();
                }
            },
            error: () => {
                for (const url of urls) {
                    subjects.get(url)?.next({ success: false, imageBase64: null });
                    subjects.get(url)?.complete();
                }
            }
        });
    }
}
